import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";

const RED = "#E3000B", YELLOW = "#FFD500";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** Customer-facing, printable invoice — reached via the private /invoice/ORDER/TOKEN link. */
export default function Invoice() {
  const { order, token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { publicApi.invoice(order, token).then(setInv).catch(() => setErr("We couldn't find that invoice. Check the link from your confirmation.")); }, [order, token]);

  if (err) return <div className="min-h-screen bg-white flex items-center justify-center p-6 text-center font-bold text-gray-700" data-testid="invoice-error">{err}</div>;
  if (!inv) return <div className="min-h-screen bg-white" />;
  const when = new Date(inv.created_at);
  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "IBM Plex Sans, system-ui, sans-serif" }} data-testid="invoice-page">
      <div className="h-2 w-full print:h-1" style={{ background: `linear-gradient(90deg, ${RED} 0 25%, ${YELLOW} 25% 50%, #0055BF 50% 75%, #00A850 75%)` }} />
      <div className="max-w-2xl mx-auto px-6 py-8 print:py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <img src="/brand/offers4bricks-logo@1000.webp" alt={inv.business.name} className="h-12 w-auto" />
          <div className="text-right">
            <div className="text-xs font-black tracking-[0.2em] text-gray-500">INVOICE / PURCHASE ORDER</div>
            <div className="text-2xl font-black" style={{ fontFamily: "Chivo, system-ui, sans-serif" }} data-testid="invoice-order-no">{inv.order_no}</div>
            <div className="text-sm text-gray-600">{when.toLocaleDateString()} · {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-8">
          <div>
            <div className="text-[11px] font-black tracking-widest text-gray-500 mb-1">SELLER (YOU)</div>
            <div className="font-bold">{inv.customer.name}</div>
            <div className="text-sm text-gray-700">{inv.customer.email}</div>
            {inv.customer.phone && <div className="text-sm text-gray-700">{inv.customer.phone}</div>}
            {inv.customer.zip && <div className="text-sm text-gray-700">ZIP {inv.customer.zip}</div>}
          </div>
          <div>
            <div className="text-[11px] font-black tracking-widest text-gray-500 mb-1">BUYER</div>
            <div className="font-bold">{inv.business.name}</div>
            {inv.business.email && <div className="text-sm text-gray-700">{inv.business.email}</div>}
            {inv.bonus_pct ? <div className="text-sm font-bold mt-2" style={{ color: "#00A850" }} data-testid="invoice-bonus">★ {inv.bonus_label || "Loyalty bonus"}: +{inv.bonus_pct}% on every set</div> : null}
            <div className="text-sm text-gray-700 mt-2"><span className="font-bold">Payout:</span> {inv.payment_method_label}{inv.payout_handle ? <span className="font-mono" data-testid="invoice-payout-handle"> · {inv.payout_handle}</span> : null}</div>
            <div className="text-sm text-gray-700"><span className="font-bold">Status:</span> <span className="uppercase" data-testid="invoice-status">{inv.status}</span></div>
          </div>
        </div>

        <table className="w-full mt-8 text-sm" data-testid="invoice-items">
          <thead>
            <tr className="text-[11px] font-black tracking-widest text-gray-500 border-b-2 border-black">
              <th className="text-left py-2">SET</th><th className="text-left py-2">NAME</th><th className="text-right py-2">QTY</th><th className="text-right py-2">PRICE PAID</th><th className="text-right py-2">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map(i => (
              <tr key={i.set_number} className="border-b border-gray-200" data-testid={`invoice-item-${i.set_number}`}>
                <td className="py-2 font-mono font-bold">#{i.set_number.replace(/-1$/, "")}</td>
                <td className="py-2">{i.name}</td>
                <td className="py-2 text-right">{i.qty}</td>
                <td className="py-2 text-right" data-testid={`invoice-price-${i.set_number}`}>{money(i.offer)}</td>
                <td className="py-2 text-right font-bold">{money(i.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="pt-4 text-right text-[11px] font-black tracking-widest text-gray-500">TOTAL PAYOUT</td>
              <td className="pt-4 text-right text-2xl font-black" style={{ fontFamily: "Chivo, system-ui, sans-serif" }} data-testid="invoice-total">{money(inv.total)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="mt-8 rounded-2xl p-4 bg-[#FFF9D6] text-sm text-gray-800 print:bg-transparent print:border print:border-gray-300">
          <div className="font-black mb-1">Terms</div>
          <ul className="list-disc pl-5 space-y-1">
            <li>{inv.business.payout_terms} Payout via {inv.payment_method_label}{inv.payout_handle ? ` (${inv.payout_handle})` : ""}.</li>
            <li>Sets must be factory sealed with the set number visible on the box. Offers are based on sealed condition and may be adjusted if the item differs.</li>
            <li>{inv.business.contact_note}</li>
          </ul>
          {inv.notes && <div className="mt-3 text-gray-600"><span className="font-bold">Your note:</span> {inv.notes}</div>}
        </div>

        <div className="mt-8 flex gap-3 no-print">
          <button onClick={() => window.print()} data-testid="invoice-print-btn"
            className="flex-1 h-12 rounded-2xl text-white font-black tracking-wide" style={{ background: RED, boxShadow: "0 5px 0 rgba(0,0,0,.18)" }}>Print / Save as PDF</button>
          <a href="/" className="h-12 px-5 rounded-2xl border-2 border-gray-200 font-bold text-gray-700 flex items-center">Home</a>
        </div>
        <p className="text-[10px] text-gray-400 text-center mt-8">LEGO® is a trademark of the LEGO Group, which does not sponsor or endorse this site. Keep this link private — it's your receipt.</p>
      </div>
    </div>
  );
}
